import { useEffect, useRef } from 'react';

import { useNavigate, useParams } from 'react-router';

import { isBefore } from 'date-fns';

import { useCreateProjectMutation } from '@app-velocity/modules/projects';
import { useTokenClaims } from '@app-velocity/request-gateway';
import { Loader } from '@app-velocity/animations';
import { useAssignNewUserToWorkspaceMutation } from '@app-velocity/modules/users';
import { useGuestAccess } from '@app-velocity/config';
import {
  useGetTeamsQuery,
  useProjectsQuery,
} from '@app-velocity/libs/teams-data-access';

export const Authorized = () => {
  const readTokenClaim = useTokenClaims();
  const userId = readTokenClaim('sub')?.toString();

  const { data: workspaces } = useProjectsQuery(userId);
  const { data: teams } = useGetTeamsQuery();

  const { data: guestData, clearGuestState } = useGuestAccess();
  const userHadNoWorkspaces = useRef<boolean>(false);
  const { mutate: createWorkspace } = useCreateProjectMutation();
  const { mutate: assignUserToGuestWorkspace, isError: isAssignmentError } =
    useAssignNewUserToWorkspaceMutation();
  const { workspaceId, teamId } = useParams<{
    teamId?: string;
    workspaceId?: string;
  }>();
  const navigate = useNavigate();

  const prepareNewWorkspace = () => {
    createWorkspace({
      name: 'My Workspace',
    });
  };

  const handleSuccessRedirect = () => {
    if (!workspaces || !teams) return;

    const workspacesWithTeams = workspaces.filter((w) => !!w.teamId);
    const sortedWorkspaces = workspacesWithTeams.sort((a, b) =>
      a.lastActivity &&
      b.lastActivity &&
      isBefore(new Date(a.lastActivity), new Date(b.lastActivity))
        ? -1
        : 1
    );

    const lastUsedWorkspaceWithTeam = sortedWorkspaces[0];

    const lastUsedTeam = teams.find(
      (t) => t.teamId === lastUsedWorkspaceWithTeam.teamId
    );

    if (!lastUsedWorkspaceWithTeam || !lastUsedTeam) return;

    navigate(`/${lastUsedTeam.teamId!}/${lastUsedWorkspaceWithTeam.id}`);
  };

  const assignToGuestWorkspace = () => {
    if (!userId || !guestData || !('workspaceId' in guestData)) return;

    assignUserToGuestWorkspace({
      projectId: guestData.workspaceId,
      userId,
    });
  };

  useEffect(() => {
    if (
      workspaceId ||
      teamId ||
      !workspaces ||
      workspaces.length > 0 ||
      !userId
    )
      return;

    if (guestData) {
      userHadNoWorkspaces.current = true;
      assignToGuestWorkspace();
    } else {
      userHadNoWorkspaces.current = true;
      prepareNewWorkspace();
    }
  }, [workspaces, workspaceId, teamId, guestData]);

  useEffect(() => {
    if (!workspaces || workspaces.length === 0 || !teams || teams.length === 0)
      return;

    if (workspaceId && teamId) return;

    handleSuccessRedirect();
  }, [workspaces, teams, workspaceId, teamId]);

  useEffect(() => {
    if (isAssignmentError !== true) return;

    prepareNewWorkspace();
    clearGuestState();
  }, [isAssignmentError]);

  return <Loader />;
};
